import { CoachingAttemptView } from '@euler/model/coaching';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useRef, useState } from 'react';

const kPage = 20;

/**
 * The trainee's attempts, newest first, paged by the last row's id so a
 * new attempt cannot shift rows between pages. A reload starts a new
 * generation: a page still in flight from the old list is dropped.
 */
export function useAttemptHistory() {
  const { coachingService } = useServiceFactory();
  const [attempts, setAttempts] = useState<CoachingAttemptView[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [pageFailed, setPageFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const generation = useRef(0);
  // Refs, not state: onEndReached fires again before a render commits.
  const exhausted = useRef(false);
  const pageInFlight = useRef(false);
  const lastId = useRef<string | null>(null);
  const reload = useCallback(() => setReloadKey(k => k + 1), []);

  useEffect(() => {
    const current = ++generation.current;
    pageInFlight.current = false;
    setFailed(false);
    setPageFailed(false);
    setLoadingMore(false);
    coachingService
      .listAttempts({ limit: kPage })
      .then(page => {
        if (current !== generation.current) return;
        exhausted.current = page.length < kPage;
        lastId.current = page.length ? page[page.length - 1].id : null;
        setAttempts(page);
      })
      .catch(error => {
        console.error('coaching attempts failed', error);
        if (current === generation.current) setFailed(true);
      });
  }, [coachingService, reloadKey]);

  const loadMore = useCallback(async () => {
    const beforeId = lastId.current;
    if (pageInFlight.current || exhausted.current || !beforeId) return;
    const current = generation.current;
    pageInFlight.current = true;
    setLoadingMore(true);
    setPageFailed(false);
    try {
      const page = await coachingService.listAttempts({
        limit: kPage,
        beforeId,
      });
      if (current !== generation.current) return;
      exhausted.current = page.length < kPage;
      if (page.length) lastId.current = page[page.length - 1].id;
      setAttempts(previous => {
        const seen = new Set((previous ?? []).map(item => item.id));
        return [
          ...(previous ?? []),
          ...page.filter(item => !seen.has(item.id)),
        ];
      });
    } catch (error) {
      console.error('coaching attempts page failed', error);
      if (current === generation.current) setPageFailed(true);
    } finally {
      if (current === generation.current) {
        pageInFlight.current = false;
        setLoadingMore(false);
      }
    }
  }, [coachingService]);

  return { attempts, failed, pageFailed, loadingMore, reload, loadMore };
}
